import { FC, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useAccessToken from "../../../../hooks/AccessToken";
import useSWR from "swr";
import { ListProduct, Pipeline } from "../../../../interfaces/Pipeline";
import { useSettingsStore } from "../../../../stores/SettingsStore";
import { MasterLog } from "../../../../components/Pipelines/Logs/MasterLog";
import moment from "moment";
import { currencyFormatter, customNumberFormat, customThousandSeparatorDot, getDataById, getDateSummary, isColorDarkFromHex } from "../../../../functions/general";
import { CustomField, SettingMultiPipelines } from "../../../../interfaces/Settings";
import { RecentChatHistoryPipeline } from "../../../../interfaces/Chat/RecentChatHistory";
import { useIntl } from "react-intl";
import { ExpandableElement } from "../../../../components/General/ExpandableElement";
import { usePipelineStore } from "../../../../stores/PipelineStore";
import { useMerchantStore } from "../../../../stores/MerchantStore";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import useUser from "../../../../hooks/User";
import BadgeTooltip from "../../../../components/General/BadgeTooltip";
import useDynamicTab from "../../../../hooks/useDynamicTab";
import { useProductStore } from "../../../../stores/ProductStore";
import Swal from "sweetalert2";
import useModalEffect from "../../../../hooks/useModalEffect";
import { DetailProductPipeline } from "../../../../components/Pipelines/DetailProductPipeline";
import { checkIsCompanyTanrise, checkIsNewProgress } from "../../../../functions/customCase";
import { useCustomeStore } from "../../../../stores/CustomeStore";
import LabelTooltip from "../../../../components/General/LabelTooltip";

interface IProducLabelMerge {
    text: string
    color: string
}
interface IProductLabelAcc {
    [key: string]: {
        productLabelColor: string;
        productLabelName: string;
        productSName: string[];
    };
}

const Logs: FC = () => {
    const { selectMultiPipeline } = usePipelineStore()
    const { code, templatePipelineId } = useParams();
    const { settings } = useSettingsStore()
    const { multiPipelines, simplePipelines } = usePipelineStore()
    const { simpleProducts } = useProductStore()
    const { simpleMerchants } = useMerchantStore()
    const [lead, setLead] = useState<Pipeline | RecentChatHistoryPipeline>({} as Pipeline)
    const [customLead, setCustomLead] = useState({} as any)
    const [dataPipeline, setDataPipeline] = useState({} as SettingMultiPipelines)
    const intl = useIntl()
    const API_URL = process.env.REACT_APP_API_URL
    const token = useAccessToken()
    const [customFields, setCustomFields] = useState<CustomField>({})
    const [showDetailProductModal, setShowDetailProductModal] = useState<boolean>(false);
    const { companyId } = useCustomeStore()
    const user = useUser()

    const fetcher = async () => {
        const headers = {
            'X-Auth-token': token,
        };

        const [
            logData,
            usersData,
        ] = await Promise.all([
            fetch(API_URL + `v2/lead/${code}`, { headers }).then((res) => {
                return res.json()
            }).then((res) => {
                if (!res.success) {
                    throw Error(res.message)
                }
                return res
            }),
            fetch(`${API_URL}users`, { headers }).then((res) => res.json()),
        ]);

        return {
            logData,
            usersData,
        };
    };

    const { data: allData, error, isLoading } = useSWR("allData", fetcher);

    const returnProductName = (productsName: string[]) => {
        return productsName.map((name, index) => index !== 0 ? `, ${name}` : name)
    }

    useEffect(() => {
        if (error) {
            Swal.fire({
                icon: 'error',
                title: error.message || "Error",
                confirmButtonText: 'Ok',
            })
        }
        if (!isLoading && !error) {
            let data = allData?.logData.data
            let users = allData?.usersData.data

            const customData: any = {
                date_created: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED' }),
                    value: moment(data.date_created).format("DD MMM YYYY HH:mm")
                },
                merchant: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: settings.merchant_title }),
                    value: returnMerchantName(data)
                },
                pic: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
                    value: data.owner_name
                },
                total_log: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TOTAL_LOGS' }),
                    value: data.lead_logs_count && customNumberFormat(data.lead_logs_count)
                },
                progress: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PROGRESS' }),
                    value: returnProgress(data)
                },
                phone: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
                    value: data.merchant_phone
                },
                age: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.AGE' }),
                    value: getDateSummary(data.lead_age)
                },
                source: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SOURCE' }),
                    value: returnSource(data)
                },
                product: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PRODUCT' }),
                    value: returnProduct(data)
                },
                label: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LABEL' }),
                    value: returnLabel(data)
                },
                nominal: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOMINAL' }) + " (" + selectMultiPipeline.currencyCode + ")",
                    value: `${data.nominal && currencyFormatter(data.nominal, selectMultiPipeline.currencyIsRounded, selectMultiPipeline.currencyCode, { showPrefix: true })}`,
                },
                product_qty: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.SUM_PRODUCTS' }),
                    value: returnQtyProduct(data)
                },
                creator: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
                    value: data.creator_name
                },
                start_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
                    value: moment(data.date_start).format("DD MMM YYYY HH:mm")
                },
                end_date: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
                    value: data.date_end ? moment(data.date_end).format("DD MMM YYYY HH:mm") : "-"
                },
                note: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
                    value: data.note ? <ExpandableElement text={(data.note).replaceAll('\n', '<br/>')} maxHeight={100} /> : "-"
                },
            }

            const newData = {
                ...customData,
                ...Object.keys(customFields).reduce((acc, key) => {
                    let value;
                    if (customFields[key].type === 'invoice_reminder' && data.custom_fields[key]) {
                        if (JSON.parse(data.custom_fields[key]).invoice_url && JSON.parse(data.custom_fields[key]).date_due) {
                            value = returnInvoiceReminder(JSON.parse(data.custom_fields[key]).invoice_url, JSON.parse(data.custom_fields[key]).date_due)
                        } else if (JSON.parse(data.custom_fields[key]).invoice_url && !JSON.parse(data.custom_fields[key]).date_due) {
                            value = <a href={JSON.parse(data.custom_fields[key]).invoice_url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
                        } else if (!JSON.parse(data.custom_fields[key]).invoice_url && JSON.parse(data.custom_fields[key]).date_due) {
                            value = moment(JSON.parse(data.custom_fields[key]).date_due).format("DD MMM YYYY")
                        } else value = "-"
                    }
                    else if (customFields[key].type === 'date') {
                        value = data.custom_fields[key] ? moment(data.custom_fields[key]).format("DD MMM YYYY") : "-";
                    } else if (customFields[key].type === 'dateTime') {
                        value = data.custom_fields[key] ? moment(data.custom_fields[key]).format('DD MMM YYYY HH:mm') : '-'
                    } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                        value = data.custom_fields[key] ? returnImage(data.custom_fields[key]) : "-";
                    } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                        value = data.custom_fields[key] ? returnFile(data.custom_fields[key]) : "-";
                    } else if (customFields[key].type === 'video' || customFields[key].type === 'videos') {
                        value = data.custom_fields[key] ? returnFile(data.custom_fields[key]) : "-";
                    } else if (customFields[key].type === 'link') {
                        value = data.custom_fields[key] ? returnLink(data.custom_fields[key] as string) : "-";
                    } else if (customFields[key].type === 'option') {
                        value = data.custom_fields[key] ? returnOption(data.custom_fields[key] as string) : "-";
                    } else if (customFields[key].type === 'paragraph') {
                        value = data.custom_fields[key] ? returnParagraph(data.custom_fields[key] as string) : "-";
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        value = data.custom_fields[key] ? returnMultiple(data.custom_fields[key]) : "-";
                    } else if (customFields[key].type === 'merchant') {
                        value = data.custom_fields[key] ? getDataById(data.custom_fields[key] as any, simpleMerchants)?.map((item: any) => item.name).join(", ") : '-';
                    } else if (customFields[key].type === 'user') {
                        value = data.custom_fields[key] ? getDataById(data.custom_fields[key] as any, users)?.map((item: any) => item.name).join(", ") : '-';
                    } else if (customFields[key].type === 'dateTimeRange') {
                        value = data.custom_fields[key] ? (data.custom_fields[key] as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                    } else if (customFields[key].type === 'pipeline') {
                        let result: any = simplePipelines.find((item: any) => item.id === (data?.info_custom_fields?.[key]?.[0] as any)?.id)

                        if (result) {
                            let name = result.name + " / " + result.nominal + " / " + moment(result.dateCreated).format('DD MMM YYYY')
                            let idTemplate = (simplePipelines as any).find((item: any) => item.id === result.id)?.leadTemplateId
                            let idPipeline = result.id
                            value = returnCustomFieldPipeline(idTemplate!, idPipeline, name);
                        } else value = "-"
                    } else if (customFields[key].type === 'meeting') {
                        let result: any[] = data.info_custom_fields?.[key] as any[]
                        if (result && result.length > 0) {
                            let formatMeeting = result.map((item: any) => `${item.merchant_name} / ${moment(item.date_meet).format('DD MMM YYYY')} / ${item.meeting_status}`).join(', ');
                            value = formatMeeting
                        } else value = "-"
                    } else if (customFields[key].type === 'time') {
                        let result = data.custom_fields[key] ? moment(data.custom_fields[key]).format('HH:mm') : '-'
                        value = result
                    } else if (customFields[key].type === 'product') {
                        value = data.custom_fields[key] ? getDataById(data.custom_fields[key] as any, simpleProducts)?.map((item: any) => item.name).join(", ") : '-';
                    } else if (customFields[key].type === 'number') {
                        value = data.custom_fields[key] ? customThousandSeparatorDot(data.custom_fields[key] as unknown as number) || "" : "-";
                    } else {
                        value = data.custom_fields[key] ? data.custom_fields[key] : "-";
                    }

                    return {
                        ...acc,
                        [key]: {
                            name: customFields[key].name,
                            value
                        }
                    };
                }, {})
            };

            setCustomLead(newData);
            setLead(data)
        }
    }, [allData, customFields, simplePipelines, simpleMerchants])

    useEffect(() => {
        var data = multiPipelines.find(pipeline => pipeline.id === templatePipelineId)
        if (data) {
            setDataPipeline(data)
            setCustomFields(data.customFields)
        }
    }, [multiPipelines, templatePipelineId, isLoading])

    const returnMerchantName = (data: Pipeline) => {
        switch (data.hot_warm_cold_status) {
            case 'hot':
                return <><span>{data.merchant_name}</span><span className="badge badge-light-danger ms-3">Hot</span></>
            case 'warm':
                return <><span>{data.merchant_name}</span><span className="badge badge-light-warning ms-3">Warm</span></>
            case 'cold':
                return <><span>{data.merchant_name}</span><span className="badge badge-light-primary ms-3">Cold</span></>
            default:
                return data.merchant_name
        }
    }

    const returnProgress = (data: Pipeline) => {
        const progressName = data.lead_progress_name;
        const subProgressName = data.lead_subprogress_name;
        const progressColor = data.lead_progress_color;

        const newProgress = checkIsCompanyTanrise(companyId, user) ? intl.formatMessage({ id: "FORM.LABEL.CONTACTED" }) : intl.formatMessage({ id: "FORM.LABEL.NEW" })
        const badgeContent = checkIsNewProgress(progressName) ? newProgress : subProgressName ? `${progressName} (${subProgressName})` : progressName;

        const badgeStyle = progressColor
            ? { backgroundColor: progressColor, color: isColorDarkFromHex(progressColor) ? "white" : "black" }
            : {};

        const progressBadge = (
            <BadgeTooltip tooltipText={badgeContent} badgeClass={`${progressColor ? '' : 'badge-light-info'}`} style={badgeStyle}>
                {badgeContent}
            </BadgeTooltip>
        );

        return (
            <div className="d-flex w-100">
                {progressBadge}
                {data.has_pending_log && (
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip>
                                {intl.formatMessage({ id: "FORM.LABEL.PENDING_LOG" })}
                            </Tooltip>
                        }
                    >
                        <div
                            className="d-flex align-items-center ms-2"
                        >
                            <i className="fas fa-info-circle text-warning fs-5"></i>
                        </div>
                    </OverlayTrigger>
                )}
            </div>
        );
    }

    const returnProduct = (data: Pipeline) => {
        const labels: IProducLabelMerge[] = data.leadProducts.flatMap((leadProduct) =>
            leadProduct.productLabels?.map((productLabel) => ({
                text: productLabel.name,
                color: productLabel.color,
            })) || []
        );

        return (
            <div className=" d-flex gap-2">
                <span className="text-primary cursor-pointer" onClick={() => { setShowDetailProductModal(true) }} dangerouslySetInnerHTML={{ __html: data.leadProducts?.map((dataProduct: ListProduct) => dataProduct.productName).join(', ') }} />
                {
                    labels.length > 0 && (
                        <LabelTooltip data={labels}>
                            {labels}
                        </LabelTooltip>
                    )
                }
            </div>
        )
    }

    const returnLabel = (data: Pipeline) => {
        var labels = data?.labels?.map((label: any) => ({ text: label.name, color: label.color })) || [];

        return (
            <>
                {
                    labels.length > 0 ? (
                        <LabelTooltip data={labels}>
                            {labels}
                        </LabelTooltip>
                    ) : "-"
                }
            </>
        )
    }

    const returnOption = (data: string) => {
        return (
            <div style={{ whiteSpace: 'pre-wrap' }}>
                <div>{data}</div>
            </div>
        )
    }

    const returnParagraph = (data: string) => {
        return (
            <ExpandableElement text={(data).replaceAll('\n', '<br/>')} maxHeight={100} />
        )
    }

    const returnQtyProduct = (data: Pipeline) => {
        if (data.leadProducts) return customNumberFormat(data.leadProducts?.reduce((total, dataProduct) => total + Number(dataProduct.leadProductQty), 0))
        else return 0
    }

    const returnSource = (data: Pipeline) => {
        let badgeText = data.lead_source_name;
        let badgeClass = 'badge-light-info'; // Default class

        switch (data.lead_source_name) {
            case 'INBOUND':
                badgeClass = 'badge-light-primary';
                badgeText = 'Inbound';
                break;
            case 'OUTBOUND':
                badgeClass = 'badge-light-success';
                badgeText = 'Outbound';
                break;
            default:
                break;
        }

        return (
            <BadgeTooltip tooltipText={badgeText} badgeClass={badgeClass}>
                {badgeText}
            </BadgeTooltip>
        );
    };

    const returnImage = (url: string | string[]) => {
        if (Array.isArray(url)) {
            return url.map((data: string, index: number) => (
                <span key={index}>
                    <a href={data} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_PICTURE" })} {index + 1}</a>
                    {index < url.length - 1 && ', '}
                </span>
            ));
        } else {
            return (
                <a href={url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_PICTURE" })}</a>
            );
        }
    };

    const returnFile = (url: string | string[]) => {
        if (Array.isArray(url)) {
            return url.map((data: string, index: number) => (
                <span key={index}>
                    <a href={data} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_FILE" })} {index + 1}</a>
                    {index < url.length - 1 && ', '}
                </span>
            ));
        } else {
            return (
                <a href={url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_FILE" })}</a>
            );
        }
    };

    const returnMultiple = (data: string) => {
        const values = data && data.toString()
        return values ? values.split(',').join(', ') : '-'
    }

    const returnInvoiceReminder = (url: string, date: string) => {
        return (
            <div>
                {moment(date).format("DD MMM YYYY")}, <a href={url} target="_blank" rel="noopener noreferrer">{intl.formatMessage({ id: "FORM.ACTION.VIEW_INVOICE" })}</a>
            </div>
        )
    }

    const returnCustomFieldPipeline = (idTemplate: string, idPipeline: string, name: string) => {
        return (
            <a href={`/pipelines/${idTemplate}/log/${idPipeline}`} target="_blank" rel="noopener noreferrer">{name}</a>
        )
    }

    const returnLink = (data: string) => {
        return (
            <a href={data} rel="noreferrer" className="btn btn-sm btn-outline btn-outline-default btn-active-light text-gray-600" target="_blank">Lihat<i className="fas fa-external-link-alt fs-6 ms-2 p-0"></i></a>
        )
    }

    useModalEffect({
        showModal: showDetailProductModal,
        setShowModal: setShowDetailProductModal,
        modalId: `detail-product-pipeline-modal-${lead?.id}`
    })

    useDynamicTab({ title: `Detail ${lead.merchant_name ?? ""} | CRM WooBlazz` });

    return (
        <>
            <div className="d-flex justify-content-between mb-5">
                <div className="text-gray-900 fs-2x fw-bold">
                    <ol className="breadcrumb text-muted fs-6 fw-bold">
                        <li className="breadcrumb-item pe-3">
                            {settings.pipeline_title}
                        </li>
                        <li className="breadcrumb-item pe-3">
                            <Link to={`/pipelines/${dataPipeline.id}`} className="text-muted text-hover-primary">
                                {dataPipeline.name}
                            </Link>
                        </li>
                        <li className="breadcrumb-item text-dark">Detail {lead.merchant_name}</li>
                    </ol>
                </div>
            </div>
            <div className="card shadow-sm mb-5">
                <div className="card-header collapsible cursor-pointer rotate collapsed" data-bs-toggle="collapse" data-bs-target="#kt_detail_pipeline" style={{ justifyContent: "left", minHeight: "50px" }}>
                    <div className="card-toolbar rotate-90">
                        <span className="svg-icon svg-icon-1">
                            <span className="svg-icon svg-icon-dark svg-icon-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M16.9 10.7L7 5V19L16.9 13.3C17.9 12.7 17.9 11.3 16.9 10.7Z" fill="black" />
                                </svg>
                            </span>
                        </span>
                    </div>
                    <h5 className="card-title ms-3 fs-4">{intl.formatMessage({ id: "FORM.LABEL.DETAILS" }, { title: settings.pipeline_title })}</h5>
                </div>
                <div id="kt_detail_pipeline" className="collapse">
                    <div className="card-body">
                        {
                            isLoading ?
                                <div className="loading-overlay">
                                    <div className="loader"></div>
                                </div>
                                :
                                <div className="row">
                                    {
                                        Object.keys(customLead).map((key, index) => {
                                            return (
                                                <div className="row pb-2 col-12 col-lg-6 col-xl-6">
                                                    <div className="col-3 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customLead[key].name}</div>
                                                    <div className="col-9" style={{ fontWeight: "500", color: "black", width: 'fit-content' }}>{customLead[key].value}</div>
                                                </div>
                                            )
                                        })
                                    }
                                </div>
                        }
                    </div>
                </div>
            </div>

            {
                !isLoading && lead.id &&
                <MasterLog lead={lead} setLead={setLead} key={code} />
            }
            {
                showDetailProductModal && <DetailProductPipeline pipeline={lead! as Pipeline} />
            }
        </>
    )
}

export { Logs }